import { useState } from "react";
import { z } from "zod";

import { Button, Table } from "@fronterahealth/frontera-ui-components";
import { FieldError } from "@fronterahealth/frontera-ui-components/ui/field";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fronterahealth/frontera-ui-components/ui/select";

import { parseJson } from "@components/FieldComponent/utils";
import { ReportFieldDisabledValue } from "@components/ReportField/ReportFieldDisabledValue";
import { ReportFieldInput } from "@components/ReportField/inputs/ReportFieldInput";
import { ReportFieldInputBaseProps } from "@components/ReportField/types";

export interface CoordinationOfCareDataRow {
  id: number;
  typeOfCollaboration: string;
  nameAndRole: string;
  datesAndFrequency: string;
}

const collaborationPresets = [
  "CCS Care Team Collaboration",
  "Collaboration Meeting",
  "Co-treatment",
  "Documentation review",
  "Educational provider/school",
  "Email collaboration",
  "Observation",
  "Phone consultation",
  "Record review as noted above in the review of records section",
  "Telehealth session",
];

const OTHER_OPTION = "Other";

const collaborationOptions = [...collaborationPresets, OTHER_OPTION];

const isPresetValue = (value: string) => value === "" || collaborationPresets.includes(value);

const getDefaultRow = (id: number): CoordinationOfCareDataRow => ({
  id,
  typeOfCollaboration: "",
  nameAndRole: "",
  datesAndFrequency: "",
});

const coordinationRowSchema = z.object({
  id: z.number(),
  typeOfCollaboration: z.string(),
  nameAndRole: z.string(),
  datesAndFrequency: z.string(),
});

const coordinationRowsSchema = z.array(coordinationRowSchema);

const EMPTY_ROWS: CoordinationOfCareDataRow[] = [];

const isLegacyEmptyTableShape = (parsed: unknown): boolean =>
  typeof parsed === "object" &&
  parsed !== null &&
  Array.isArray((parsed as { data?: unknown }).data) &&
  (parsed as { data: unknown[] }).data.length === 0;

const parseRows = (value: string | null | undefined) => {
  const parsed = value ? parseJson<unknown>(value) : EMPTY_ROWS;
  return coordinationRowsSchema.safeParse(isLegacyEmptyTableShape(parsed) ? EMPTY_ROWS : parsed);
};

export const ReportFieldCoordinationOfCare: React.FC<ReportFieldInputBaseProps> = ({
  value,
  onChange,
  disabled,
  variant,
}) => {
  const result = parseRows(value);
  const parsedRows = result.success && result.data.length > 0 ? result.data : [getDefaultRow(1)];

  // Ephemeral, non-persisted UI state: tracks which rows the user has explicitly
  // put into "Other" free-text mode, since an empty typeOfCollaboration otherwise
  // looks identical to "nothing selected" (the whole class of bug this migration fixes).
  const [otherModeRowIds, setOtherModeRowIds] = useState<Set<number>>(
    () => new Set(parsedRows.filter((row) => !isPresetValue(row.typeOfCollaboration)).map((row) => row.id)),
  );

  if (!result.success) return <FieldError errors={result.error.issues} />;

  const rows = parsedRows;

  const saveRows = (updatedRows: CoordinationOfCareDataRow[]) => onChange(JSON.stringify(updatedRows));

  const updateRow = (rowId: number, field: keyof CoordinationOfCareDataRow, fieldValue: string) =>
    saveRows(rows.map((row) => (row.id === rowId ? { ...row, [field]: fieldValue } : row)));

  const handleAddRow = () => {
    const newId = Math.max(...rows.map((row) => row.id), 0) + 1;
    saveRows([...rows, getDefaultRow(newId)]);
  };

  const handleDeleteRow = (rowId: number) => {
    setOtherModeRowIds((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
    saveRows(rows.filter((row) => row.id !== rowId));
  };

  const handleCollaborationTypeChange = (rowId: number, selected: string | null) => {
    if (selected === OTHER_OPTION) {
      setOtherModeRowIds((prev) => new Set(prev).add(rowId));
      updateRow(rowId, "typeOfCollaboration", "");
      return;
    }
    setOtherModeRowIds((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.delete(rowId);
      return next;
    });
    updateRow(rowId, "typeOfCollaboration", selected ?? "");
  };

  return (
    <div className="space-y-4">
      <div className="overflow-visible">
        <Table
          defaultItemsPerPage={20}
          cellClassName="cursor-default overflow-visible"
          data={rows}
          columns={[
            {
              dataIndex: "typeOfCollaboration",
              key: "typeOfCollaboration",
              title: "Type of Collaboration/Coordination & Description",
              className: "border-r align-top w-1/3 overflow-visible",
              render: (_, row) => {
                if (disabled) {
                  return <ReportFieldDisabledValue value={row.typeOfCollaboration} />;
                }

                const isOther = otherModeRowIds.has(row.id) || !isPresetValue(row.typeOfCollaboration);
                const selectValue = isOther ? OTHER_OPTION : row.typeOfCollaboration || null;

                return (
                  <>
                    <Select value={selectValue} onValueChange={(next) => handleCollaborationTypeChange(row.id, next)}>
                      <SelectTrigger className="w-full text-sm" variant={variant} disabled={disabled}>
                        <SelectValue placeholder="--" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[28rem]">
                        <SelectGroup>
                          {selectValue && <SelectItem value={null}>--</SelectItem>}
                          {collaborationOptions.map((option) => (
                            <SelectItem key={option} value={option} className="whitespace-normal">
                              {option}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {isOther && (
                      <div className="mt-2">
                        <ReportFieldInput
                          value={row.typeOfCollaboration}
                          placeholder="Describe the type of collaboration"
                          onChange={(value) => updateRow(row.id, "typeOfCollaboration", value)}
                        />
                      </div>
                    )}
                  </>
                );
              },
            },
            {
              dataIndex: "nameAndRole",
              key: "nameAndRole",
              title: "Name and/or Role",
              className: "border-r align-top w-1/3",
              render: (_, row) => (
                <ReportFieldInput
                  value={row.nameAndRole}
                  placeholder="Enter name and/or role"
                  onChange={(value) => updateRow(row.id, "nameAndRole", value)}
                  disabled={disabled}
                  variant={variant}
                  className="text-sm"
                />
              ),
            },
            {
              dataIndex: "datesAndFrequency",
              key: "datesAndFrequency",
              title: "Date(s) and/or frequency of Collaboration",
              className: "align-top w-1/3",
              render: (_, row) => (
                <ReportFieldInput
                  value={row.datesAndFrequency}
                  placeholder="Enter dates and/or frequency"
                  onChange={(value) => updateRow(row.id, "datesAndFrequency", value)}
                  disabled={disabled}
                  variant={variant}
                  className="text-sm"
                />
              ),
            },
          ]}
        />
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Button appearance="secondary" text="Add Row" onClick={handleAddRow} />
          {rows.length > 1 && (
            <Button
              appearance="link"
              text="Delete Last Row"
              onClick={() => handleDeleteRow(rows[rows.length - 1].id)}
              className="text-red-600 hover:text-red-700"
            />
          )}
        </div>
      )}
    </div>
  );
};
